import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { task } from 'ember-concurrency'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type SessionService from 'teacher-ui-client/services/session'
import type RouterService from '@ember/routing/router-service'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type Teacher from 'teacher-ui-client/models/teacher'
import { uniq } from 'lodash'
import type { TeacherItem } from 'teacher-ui-client/components/tables/peaks-progress/teachers'
import { TrackedArray } from 'tracked-built-ins'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import { hasId } from 'teacher-ui-client/utils/type-guards'

const MAX_CONCURRENCY = 3

export default class FastPhonicsReportingProgressReviewTeachersRoute extends Route {
  queryParams = {
    sort: {
      replace: true,
    },
  }

  @service studentEvents!: StudentEventsService

  @service gradeSets!: GradeSetsService

  @service session!: SessionService

  @service router!: RouterService

  get schoolId() {
    return this.session.currentSchool.id
  }

  beforeModel() {
    /* Any attempt to go to the teachers tab should be forced to use whole school scope. Other scopes don't make sense
     * on this page
     */
    const { uiScope } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    if (uiScope.scope !== 'school') {
      void this.router.transitionTo(this.routeName, 'school', this.schoolId)
    }
  }

  model() {
    const loadTeacherDataTask = this.loadTeacherData
    void loadTeacherDataTask.cancelAll()

    const {
      scopedData: { teachers },
    } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    // Immediately return an empty array for teachers. For each teacher record, we'll load extra teacher data
    // progressively and add to the model as it comes in, in a non-blocking asynchronous way. As each item is added to
    // the model, it will then display in the table.
    const teacherItems: TrackedArray<TeacherItem> = new TrackedArray([])
    teachers.forEach((teacherRecord) => {
      void this.loadTeacherData.perform(teacherRecord, teacherItems)
      return
    })
    return { teacherItems, loadTeacherDataTask }
  }

  /**
   * With a given teacher record, load the aggregate data and add it to the given teachers array
   */
  loadTeacherData = task(
    { enqueue: true, maxConcurrency: MAX_CONCURRENCY },
    async (teacherRecord: Teacher, teacherItems: TrackedArray<TeacherItem>) => {
      const { gradeSets } = this

      const students = teacherRecord.studentsForSubscriptionType(SubscriptionType.Reading).filter(hasId)
      if (isEmpty(students) || !teacherRecord.id) return // Skip teachers with no reading students.

      const uniqStudentGrade = uniq(students.map((student) => student.gradePosition))

      // Grades
      const grades = uniqStudentGrade
        .sort((a, b) => a - b)
        .map((gradePosition) => gradeSets.nameFromGradeSetByPosition(gradePosition))
        .join(', ')

      // Get fast phonics progress data for the teachers' students
      const scope: StudentsScope = { scope: 'students', ids: students.map((student) => student.id) }
      const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

      const peaksProgressData = await this.studentEvents.peaksProgress('fast_phonics', scope, period)
      if (!peaksProgressData) return
      const summary = peaksProgressData.summary

      const teacherData: TeacherItem = {
        id: teacherRecord.id,
        firstName: teacherRecord.firstName,
        lastName: teacherRecord.lastName,
        grades: grades,
        studentsSummary: summary,
      }

      // Push the teacher data to the array
      teacherItems.push({ ...teacherData })
    },
  )
}
